// src/components/AlertsPanel.jsx
import { useDataStore } from '../stores/dataStore'
import { formatRelativeTime } from '../utils/format'
import { useEffect } from 'react'
import ActionBar from './ConnectionActions'

// Professional SVG icons for alerts
const AlertIcons = {
  warning: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  critical: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  info: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="16"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  unencrypted: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 019.9-1"/>
      <circle cx="12" cy="16" r="1"/>
    </svg>
  ),
  network: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14"/>
      <line x1="4" y1="10" x2="4" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12" y2="3"/>
      <line x1="20" y1="21" x2="20" y2="16"/>
      <line x1="20" y1="12" x2="20" y2="3"/>
      <line x1="1" y1="14" x2="7" y2="14"/>
      <line x1="9" y1="8" x2="15" y2="8"/>
      <line x1="17" y1="16" x2="23" y2="16"/>
    </svg>
  ),
  desktop: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
  globe: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
    </svg>
  ),
  location: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
}

// Map alert rule IDs to appropriate icons
const getAlertIcon = (ruleId, severity) => {
  const iconMap = {
    unencrypted_connection: AlertIcons.unencrypted,
    unusual_low_port: AlertIcons.warning,
    very_high_port: AlertIcons.info,
    unknown_location: AlertIcons.globe,
    flagged_country: AlertIcons.critical,
    remote_desktop: AlertIcons.desktop,
  }
  const IconComponent = iconMap[ruleId] || AlertIcons[severity] || AlertIcons.info
  return <IconComponent />
}

const SEVERITY_CONFIG = {
  info:     { color: 'var(--accent-blue)',  bg: 'rgba(77,157,224,0.08)',  border: 'rgba(77,157,224,0.2)',  label: 'Info' },
  warning:  { color: 'var(--accent-amber)', bg: 'rgba(245,166,35,0.08)', border: 'rgba(245,166,35,0.2)', label: 'Warning' },
  critical: { color: 'var(--accent-red)',   bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.2)',  label: 'Critical' },
}

export default function AlertsPanel({ onDismiss }) {
  const alerts = useDataStore(s => s.alerts)
  const dismissAlert = useDataStore(s => s.dismissAlert)

  const active    = alerts.filter(a => !a.dismissed)

  // Auto-dismiss non-red alerts after 2 minutes
  useEffect(() => {
    const timers = new Map()
    
    console.log('Active alerts:', active.map(a => ({ id: a.id, severity: a.severity })))
    
    active.forEach(alert => {
      // Skip red/critical alerts only (auto-dismiss amber/warning after 2 minutes)
      if (alert.severity === 'critical') {
        console.log('Skipping auto-dismiss for critical alert:', alert.id)
        return
      }
      
      console.log('Setting auto-dismiss timer for non-critical alert:', alert.id, 'severity:', alert.severity)
      
      const existingTimer = timers.get(alert.id)
      if (existingTimer) clearTimeout(existingTimer)
      
      const timer = setTimeout(() => {
        console.log('Auto-dismissing alert:', alert.id)
        dismissAlert(alert.id)
        timers.delete(alert.id)
      }, 2 * 60 * 1000) // 2 minutes
      
      timers.set(alert.id, timer)
    })
    
    return () => {
      console.log('Cleaning up timers')
      timers.forEach(timer => clearTimeout(timer))
    }
  }, [active, dismissAlert])

  if (active.length === 0) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
        height:'100%', flexDirection:'column', gap:12 }}>
        <div style={{ fontSize:40 }}>✅</div>
        <div style={{ color:'var(--text-secondary)', fontSize:15, fontWeight:500 }}>All clear</div>
        <div style={{ color:'var(--text-tertiary)', fontSize:12, textAlign:'center', maxWidth:300 }}>
          Netwoke monitors your connections in real time. Any unusual activity will appear here.
        </div>
      </div>
    )
  }

  return (
    <div style={{ height:'100%', overflowY:'auto', padding:'12px 16px',
      display:'flex', flexDirection:'column', gap:6 }}>

      {active.length > 0 && (
        <>
          <SectionLabel text={`${active.length} active ${active.length === 1 ? 'alert' : 'alerts'}`} />
          {active.map((alert, i) => (
            <AlertCard key={alert.id} alert={alert} index={i} onDismiss={onDismiss} />
          ))}
        </>
      )}

    </div>
  )
}

function SectionLabel({ text, style }) {
  return (
    <div style={{
      fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)',
      padding: '0 4px', marginBottom: 2, ...style,
    }}>{text}</div>
  )
}

function AlertCard({ alert, index, onDismiss, dismissed }) {
  const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info
  const actionTarget = toActionTarget(alert)
  const GlobeIcon = AlertIcons.globe
  const LocationIcon = AlertIcons.location

  return (
    <div className="animate-fade-in" style={{
      background: dismissed ? 'transparent' : cfg.bg,
      border: `1px solid ${dismissed ? 'var(--border-subtle)' : cfg.border}`,
      borderRadius: 12, padding: '14px 16px',
      opacity: dismissed ? 0.45 : 1,
      animationDelay: `${Math.min(index, 5) * 40}ms`,
      animationFillMode: 'both',
    }}>
      <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
        <div style={{ 
          flexShrink:0, lineHeight:1, marginTop:1, 
          color: dismissed ? 'var(--text-tertiary)' : cfg.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {getAlertIcon(alert.ruleId, alert.severity)}
        </div>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
            <span style={{
              fontSize:10, padding:'2px 6px', borderRadius:4,
              background: dismissed ? 'var(--bg-elevated)' : `${cfg.color}22`,
              color: dismissed ? 'var(--text-tertiary)' : cfg.color,
              fontFamily:'var(--font-mono)', fontWeight:500,
              textTransform:'uppercase', letterSpacing:'0.05em',
            }}>{cfg.label}</span>
            <span style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>
              {alert.appName}
            </span>
            <span style={{ fontSize:11, color:'var(--text-tertiary)', marginLeft:'auto',
              fontFamily:'var(--font-mono)' }}>
              {formatRelativeTime(alert.timestamp)}
            </span>
          </div>

          <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.55, marginBottom:10 }}>
            {alert.message}
          </div>

          <div style={{ display:'flex', gap:12, fontSize:11,
            color:'var(--text-tertiary)', fontFamily:'var(--font-mono)', alignItems:'center' }}>
            {alert.domain  && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 14, height: 14, opacity: 0.6, display: 'flex', alignItems: 'center' }}>
                  <GlobeIcon />
                </span>
                {alert.domain}
              </span>
            )}
            {alert.country && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 14, height: 14, opacity: 0.6, display: 'flex', alignItems: 'center' }}>
                  <LocationIcon />
                </span>
                {alert.country}
              </span>
            )}
            {alert.remoteIP && (
              <span style={{ 
                color: alert.severity === 'info' ? 'var(--accent-teal)' : 'inherit',
                fontWeight: alert.severity === 'info' ? 600 : 'inherit'
              }}>
                {alert.remoteIP}
              </span>
            )}
          </div>

          {!dismissed && (
            <ActionBar conn={actionTarget} />
          )}
        </div>

        {!dismissed && onDismiss && (
          <button onClick={e => { e.stopPropagation(); onDismiss(alert.id) }} style={{
            background:'transparent', border:'none',
            color:'var(--text-tertiary)', cursor:'pointer',
            fontSize:16, padding:'2px 4px', borderRadius:4,
            lineHeight:1, flexShrink:0, transition:'color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color='var(--text-primary)'}
            onMouseLeave={e => e.currentTarget.style.color='var(--text-tertiary)'}
            title="Dismiss"
          >×</button>
        )}
      </div>

      {!dismissed && (
        <div style={{ marginTop:12, padding:'8px 10px',
          background:'rgba(0,0,0,0.2)', borderRadius:8,
          fontSize:11, color:'var(--text-tertiary)' }}>
          <span style={{ color:'var(--text-secondary)', fontWeight:500 }}>What to do: </span>
          <span dangerouslySetInnerHTML={{ __html: getRecommendation(alert.ruleId, alert) }} />
        </div>
      )}
    </div>
  )
}

function toActionTarget(alert) {
  return {
    appName: alert.appName || 'Unknown App',
    pid: alert.pid ?? parsePidFromAlertId(alert.id),
    remoteIP: alert.remoteIP,
    domain: alert.domain,
    country: alert.country,
  }
}

function parsePidFromAlertId(id) {
  const pid = Number.parseInt(String(id || '').split(':')[1], 10)
  return Number.isFinite(pid) ? pid : null
}

function getRecommendation(ruleId, alert) {
  const r = {
    unencrypted_connection: 'If this is a website, try accessing it with "https://" instead. Contact the website owner if the problem persists.',
    unusual_low_port:       'Check if you recently installed new software or changed settings. If unexpected, consider running a malware scan.',
    flagged_country:        'If you did not expect this connection, consider quitting the app and investigating what it\'s doing.',
    unknown_location:       'This may be a private or corporate server. If the app is from a trusted source, this is likely fine.',
    remote_desktop:         'If you did not start a remote session yourself, disconnect from the internet and check your security settings immediately.',
  }
  
  let recommendation = r[ruleId] || 'Monitor the situation and dismiss if you expect this behavior.'
  
  // Highlight IP addresses in teal for info severity alerts
  if (alert && alert.severity === 'info' && alert.remoteIP) {
    // Escape special regex characters in IP for proper replacement
    const escapedIP = alert.remoteIP.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    recommendation = recommendation.replace(
      new RegExp(`\\b${escapedIP}\\b`, 'g'),
      `<span style="color: var(--accent-teal); font-weight: 600;">${alert.remoteIP}</span>`
    )
  }
  
  return recommendation
}
